import { useState, useEffect, useCallback } from 'react'
import {
  clearProductMetricsCookie,
  flushProductMetricsToServer,
  recordLandingSession,
} from '../utils/conversionMetrics'

export interface CookiePreferences {
  essential: boolean
  functional: boolean
  analytics: boolean
  healthData: boolean
}

export interface CookieConsentData {
  preferences: CookiePreferences
  consentDate: string | null
  hasConsented: boolean
}

type ConsentSyncState = 'idle' | 'loading' | 'synced' | 'error'

export const useCookieConsent = () => {
  const [syncState, setSyncState] = useState<ConsentSyncState>('idle')
  const [consentData, setConsentData] = useState<CookieConsentData>({
    preferences: {
      essential: true,
      functional: false,
      analytics: false,
      healthData: false
    },
    consentDate: null,
    hasConsented: false
  })
  const [reConsentNeeded] = useState(false)

  // Load consent data from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem('cookieConsent')
    const savedDate = localStorage.getItem('cookieConsentDate')
    
    if (savedConsent) {
      try {
        const preferences = JSON.parse(savedConsent)
        setConsentData({
          preferences,
          consentDate: savedDate,
          hasConsented: true
        })
      } catch (error) {
        console.error('Error parsing cookie consent data:', error)
        localStorage.removeItem('cookieConsent')
        localStorage.removeItem('cookieConsentDate')
      }
    }
  }, [])

  // Consent sync is now local-only (no auth required for landing page).
  const syncConsentFromBackend = useCallback(async () => {
    setSyncState('synced')
  }, [])

  const updateConsent = async (preferences: CookiePreferences) => {
    const consentDate = new Date().toISOString()
    
    // Save to localStorage (fallback)
    localStorage.setItem('cookieConsent', JSON.stringify(preferences))
    localStorage.setItem('cookieConsentDate', consentDate)
    
    // Update state
    setConsentData({
      preferences,
      consentDate,
      hasConsented: true
    })

    // Apply cookie settings
    applyCookieSettings(preferences)

    if (preferences.analytics) {
      recordLandingSession()
      void flushProductMetricsToServer()
    }
  }

  const withdrawConsent = async () => {
    // Update local state
    const preferences: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      healthData: false
    }

    localStorage.removeItem('cookieConsent')
    localStorage.removeItem('cookieConsentDate')
    
    // Remove non-essential cookies
    document.cookie = "functional_cookies=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
    document.cookie = "analytics_cookies=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
    clearProductMetricsCookie()
    
    setConsentData({
      preferences,
      consentDate: null,
      hasConsented: false
    })

    applyCookieSettings(preferences)
  }

  const clearConsent = async () => {
    await withdrawConsent()
  }

  const hasConsentFor = (cookieType: keyof CookiePreferences): boolean => {
    return consentData.preferences[cookieType]
  }

  return {
    consentData,
    updateConsent,
    clearConsent,
    withdrawConsent,
    hasConsentFor,
    syncState,
    syncConsentFromBackend,
    reConsentNeeded,
  }
}

// Utility functions for cookie management
export const applyCookieSettings = (preferences: CookiePreferences) => {
  // Set functional cookies if consented
  if (preferences.functional) {
    document.cookie = "functional_cookies=true; path=/; max-age=31536000; SameSite=Strict; Secure"
  } else {
    document.cookie = "functional_cookies=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
  }

  // Set analytics cookies if consented
  if (preferences.analytics) {
    document.cookie = "analytics_cookies=true; path=/; max-age=63072000; SameSite=Strict; Secure"
  } else {
    document.cookie = "analytics_cookies=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
    clearProductMetricsCookie()
  }
}

export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift()
    return cookieValue || null
  }
  return null
}

export const setCookie = (name: string, value: string, options: {
  maxAge?: number
  expires?: Date
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
} = {}): void => {
  let cookieString = `${name}=${value}`
  
  if (options.maxAge !== undefined) {
    cookieString += `; max-age=${options.maxAge}`
  }
  
  if (options.expires) {
    cookieString += `; expires=${options.expires.toUTCString()}`
  }
  
  if (options.path) {
    cookieString += `; path=${options.path}`
  }
  
  if (options.domain) {
    cookieString += `; domain=${options.domain}`
  }
  
  if (options.secure) {
    cookieString += `; secure`
  }
  
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`
  }
  
  document.cookie = cookieString
}